/**
 * Startup: progress, diagnostics, and never a blank screen.
 *
 * The owner reported an iPhone that sat on the loading bar indefinitely. The
 * cause was structural rather than exotic: `boot()` was one long synchronous
 * function with no error handling, and the splash was removed on its last
 * line. Anything that threw on the way - a WebGL context the device refused,
 * a shader Safari would not compile, memory pressure - left an animated
 * progress bar on screen forever, with the actual error sitting in a console
 * nobody on a phone can open.
 *
 * So three things live here:
 *
 * - **Stages.** Startup reports what it is doing, and yields to the browser
 *   between steps so the report is actually painted. A phone spends several
 *   seconds generating textures and building the world; silence for that long
 *   is indistinguishable from a hang.
 * - **A failure screen.** Any throw produces something readable and
 *   actionable, with the diagnostics attached, rather than the bar.
 * - **Diagnostics.** Collected from the first line, so the failure screen can
 *   say what the device actually supports.
 */

export interface BootDiagnostics {
  userAgent: string;
  screen: string;
  devicePixelRatio: number;
  webgl2: boolean;
  webgl1: boolean;
  renderer: string;
  maxTextureSize: number;
  touch: boolean;
  storage: boolean;
  stage: string;
  elapsedMs: number;
}

const STAGES = [
  'Loading simulation',
  'Building Maple Grove',
  'Painting surfaces',
  'Putting traffic on the roads',
  'Preparing your character',
  'Starting game',
] as const;

export type BootStage = typeof STAGES[number];

const started = typeof performance !== 'undefined' ? performance.now() : 0;
let currentStage: string = 'Starting up';

/**
 * Probe what the device can actually do, before anything tries to use it.
 *
 * Deliberately creates and discards its own context: asking the question with
 * the real renderer would mean the answer arrives only after the thing that
 * needs it has already thrown.
 */
export function collectDiagnostics(): BootDiagnostics {
  let webgl2 = false;
  let webgl1 = false;
  let rendererName = 'unknown';
  let maxTextureSize = 0;
  try {
    const canvas = document.createElement('canvas');
    const gl2 = canvas.getContext('webgl2');
    webgl2 = gl2 !== null;
    const gl = gl2 ?? canvas.getContext('webgl');
    webgl1 = gl !== null;
    if (gl) {
      maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
      // WEBGL_debug_renderer_info is gated in some browsers; the unmasked name
      // is worth having when it is available and not worth failing over.
      const debug = gl.getExtension('WEBGL_debug_renderer_info');
      rendererName = debug
        ? String(gl.getParameter(debug.UNMASKED_RENDERER_WEBGL))
        : String(gl.getParameter(gl.RENDERER));
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
  } catch { /* a probe that throws is itself a diagnostic */ }

  let storage = false;
  try {
    window.localStorage.setItem('life-empire:probe', '1');
    window.localStorage.removeItem('life-empire:probe');
    storage = true;
  } catch { /* private browsing, or site data blocked */ }

  return {
    userAgent: navigator.userAgent,
    screen: `${window.innerWidth}x${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio,
    webgl2,
    webgl1,
    renderer: rendererName,
    maxTextureSize,
    touch: navigator.maxTouchPoints > 0,
    storage,
    stage: currentStage,
    elapsedMs: Math.round((typeof performance !== 'undefined' ? performance.now() : 0) - started),
  };
}

/**
 * Report a stage and give the browser a chance to paint it.
 *
 * A double rAF rather than a timeout: one frame schedules the paint, the
 * second lands after it. With a single one the text changes and the screen
 * still shows the previous stage until the next long block finishes, which is
 * the failure this whole module exists to prevent.
 */
export async function stage(name: BootStage): Promise<void> {
  currentStage = name;
  const label = document.getElementById('le-boot-stage');
  const bar = document.getElementById('le-boot-fill');
  if (label) label.textContent = name;
  if (bar) {
    const index = STAGES.indexOf(name);
    const fraction = index < 0 ? 0 : (index + 1) / STAGES.length;
    bar.style.width = `${Math.round(fraction * 100)}%`;
  }
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/** Take the splash down once the world is actually on screen. */
export function finishBoot(): void {
  const overlay = document.getElementById('le-boot');
  if (!overlay) return;
  overlay.classList.add('gone');
  window.setTimeout(() => overlay.remove(), 700);
}

/**
 * Replace the splash with something the owner can act on.
 *
 * The rule this enforces: never leave anyone looking at a progress bar that
 * is not progressing. An error they can read and send on is worth far more
 * than a bar that is still politely sweeping.
 */
export function showFatalError(error: unknown, diagnostics: BootDiagnostics): void {
  const overlay = document.getElementById('le-boot');
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error && error.stack ? error.stack : '';

  const advice = !diagnostics.webgl1
    ? 'This device or browser is not providing WebGL at all. On iOS, check '
      + 'Settings > Safari > Advanced > Experimental Features, or try a '
      + 'different browser.'
    : !diagnostics.webgl2
      ? 'This browser has WebGL 1 but not WebGL 2, which the game needs. '
        + 'iOS 15 or later supports it; older versions do not.'
      : 'The graphics driver accepted a context but something failed while '
        + 'setting the scene up. The details below are the useful part.';

  const panel = document.createElement('div');
  panel.className = 'le-fatal';
  panel.innerHTML = `
    <div class="card">
      <div class="mark">LIFE EMPIRE</div>
      <h1>The game could not start</h1>
      <p class="advice"></p>
      <p class="reason"></p>
      <details>
        <summary>Technical details</summary>
        <pre></pre>
      </details>
      <button type="button" class="retry">Try again</button>
    </div>`;
  (panel.querySelector('.advice') as HTMLElement).textContent = advice;
  (panel.querySelector('.reason') as HTMLElement).textContent =
    `It failed while: ${diagnostics.stage}. ${message}`;
  (panel.querySelector('pre') as HTMLElement).textContent =
    [
      `stage        ${diagnostics.stage}`,
      `elapsed      ${diagnostics.elapsedMs} ms`,
      `webgl2       ${diagnostics.webgl2}`,
      `webgl1       ${diagnostics.webgl1}`,
      `renderer     ${diagnostics.renderer}`,
      `maxTexture   ${diagnostics.maxTextureSize}`,
      `screen       ${diagnostics.screen} @${diagnostics.devicePixelRatio}x`,
      `touch        ${diagnostics.touch}`,
      `storage      ${diagnostics.storage}`,
      `agent        ${diagnostics.userAgent}`,
      '',
      stack,
    ].join('\n');
  (panel.querySelector('.retry') as HTMLButtonElement).onclick = () => {
    window.location.reload();
  };

  if (overlay) {
    overlay.replaceChildren(panel);
    overlay.classList.remove('gone');
  } else {
    document.body.appendChild(panel);
  }
  // Still log it: on a desktop the console is where a developer will look.
  console.error('[LIFE EMPIRE] startup failed', error, diagnostics);
}

/**
 * Whether to skip the post-processing stack entirely.
 *
 * Ambient occlusion is the most demanding thing in the build and the most
 * likely to fail on a mobile driver - it wants float render targets and a
 * long shader. A working plain render beats a beautiful blank screen, so a
 * phone gets one unless the player asks otherwise with `?fx=1`.
 */
export function shouldSkipPostProcessing(touchPrimary: boolean): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('fx') === '1') return false;
    if (params.get('fx') === '0') return true;
  } catch { /* a malformed query string is not worth failing over */ }
  return touchPrimary;
}

/**
 * Register the service worker, when there is one to register.
 *
 * Guarded on a secure context: the Electron build runs from file:// where
 * service workers do not exist, and an unhandled rejection there would trip
 * the startup error screen for a feature that is not even relevant.
 *
 * Failure is never fatal. The worker makes the game installable and lets it
 * open after a bad signal; it is not required to play.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  if (!window.isSecureContext) return;
  if (location.protocol !== 'https:' && location.hostname !== 'localhost'
    && location.hostname !== '127.0.0.1') return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js').catch((error: unknown) => {
      console.warn('[LIFE EMPIRE] service worker not registered', error);
    });
  });
}
